/**
 * Global dictation manager hook for handling voice-to-text transcription.
 *
 * Several components mount this hook at once — Assessment's floating button,
 * the Indy composer, and its mic button — but there is one microphone and one
 * transcription service. Cross-instance state therefore lives in two shared
 * places: UI state in Jotai atoms, and the open turn's write-state in the
 * module-scoped `session` below. Per-instance refs for that state let the
 * instance that started dictation and the instance whose handlers happened to
 * bind last disagree about what had been written where.
 */
import { toast } from "@indigo-components";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  dictationActiveSectionAtom,
  dictationAudioLevelAtom,
  dictationSpeechDetectedAtom,
  dictationStatusAtom,
} from "~/atoms/dictation";
import { useCurrentUser } from "~/auth/UserContext";
import { INDY_COMPOSER_ATTRIBUTE } from "~/pages/indy/indyComposerTarget";
import { cleanDictationUtterance } from "~/services/dictationCleanup";
import { useFeatureFlag } from "~/services/featureFlags";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import {
  type AudioAnalysisData,
  type DictationSurface,
  isStreamingSurface,
  webrtcTranscriptionService,
} from "~/services/webrtcTranscription";
import { type SpeechThresholds } from "~/utils/audioAnalysis";
import {
  DictationCleanupQueue,
  type TargetedRegion,
} from "~/utils/dictationCleanupQueue";
import {
  decapitalizeContinuation,
  isRegionIntact,
  placeTranscript,
  replaceRegion,
  shiftRegion,
} from "~/utils/dictationTranscript";
import {
  checkMicrophonePermission,
  isBrowserSupported,
  isTextInputElement,
  setInputValue,
} from "~/utils/textInputUtils";

// Throttle audio analysis updates to ~15fps for smooth animation without React overhead
const AUDIO_ANALYSIS_THROTTLE_MS = 67;

/**
 * How long `flushDictation` waits for a streaming dictation to settle before
 * saving anyway. Long enough for a final (and usually one cleanup) to land,
 * short enough that a save never feels stuck on the microphone.
 */
const FLUSH_TIMEOUT_MS = 2000;

type TargetElement = HTMLInputElement | HTMLTextAreaElement;

/**
 * Which side of the composer boundary a field sits on.
 *
 * The dictation surfaces are scoped to opposite sides of exactly one line —
 * Indy writes inside `[data-indy-composer]`, the Assessment surfaces outside
 * it — so two keys cover every field, and three surfaces collapse to two.
 */
type FenceKey = "indy" | "page";

const fenceKeyFor = (surface: DictationSurface): FenceKey =>
  surface === "indy" ? "indy" : "page";

/**
 * A span of written text and the field it lives in.
 *
 * Offsets only mean anything within one field, and a physician dictating into
 * a form moves between fields mid-recording — so every span carries its own
 * element rather than borrowing whichever one is current when a final or a
 * cleanup finally lands.
 */
type Span = TargetedRegion<TargetElement>;

/**
 * The open dictation turn, shared across every hook instance.
 *
 * `insertionPoint` belongs to the Assessment path (cursor where deltas
 * started); `openSpan` belongs to the streaming path (the span we last wrote,
 * and its field). Each path only reads its own field.
 */
const session = {
  currentTranscript: "",
  accumulatedDelta: "",
  insertionPoint: null as number | null,
  openSpan: null as Span | null,
  target: null as TargetElement | null,
  /**
   * The last field focused on each side of the composer boundary.
   *
   * Two slots, not one. A single slot cannot serve both surfaces: the Indy
   * panel autofocuses its composer when it opens, so it would overwrite the
   * report field an assessment dictation is about to need, and the utterance
   * would be dropped for want of a fallback. Entries are classified by which
   * fence accepts the element, never by whichever surface happens to be
   * current — `getSurface()` is sticky between sessions, so reading it here
   * files pre-gesture focus under the *previous* session's surface.
   */
  lastFocused: { indy: null, page: null } as Record<
    FenceKey,
    HTMLElement | null
  >,
  cleanupQueue: null as DictationCleanupQueue<TargetElement> | null,
  /**
   * Spans sealed by a commit, oldest first, each awaiting its final.
   *
   * Sealing happens at COMMIT time, not final time, because the physician
   * keeps talking through the round trip: by the time utterance N's final
   * arrives, utterance N+1 is already streaming. Replacing the *live* span
   * then wipes the words currently being spoken and the reformat reads as a
   * glitch. A sealed span pins final N to exactly utterance N's text, while
   * N+1 streams into a fresh span after it. Finals map to sealed spans in
   * arrival order — the Realtime API returns them in commit order, so there
   * is exactly one entry per commit. A commit whose utterance never wrote
   * (silence, or whitespace-only deltas) seals a null placeholder: skipping
   * the push entirely would let that commit's final consume the NEXT
   * sentence's span, and every final after it would land one sentence early.
   */
  sealedSpans: [] as (Span | null)[],
};

/**
 * Callers parked on the sealed-span FIFO draining — a save waiting for the
 * sentence just spoken to come back corrected before it serialises the form.
 * Module-scoped for the same reason `session` is: the waiter and the hook
 * instance whose handlers consume the finals are rarely the same instance.
 */
let drainWaiters: Array<() => void> = [];

/** Release anyone waiting once the last sealed span has had its final. */
const notifyIfDrained = (): void => {
  if (session.sealedSpans.length > 0 || drainWaiters.length === 0) {
    return;
  }
  const waiting = drainWaiters;
  drainWaiters = [];
  for (const resolve of waiting) {
    resolve();
  }
};

/** Wait for every committed utterance to have landed, or give up. */
const waitForSealedSpans = (timeoutMs: number): Promise<void> => {
  if (session.sealedSpans.length === 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const settle = (): void => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      drainWaiters = drainWaiters.filter((waiter) => waiter !== settle);
      resolve();
    }, timeoutMs);
    drainWaiters.push(settle);
  });
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const resetSession = (): void => {
  session.currentTranscript = "";
  session.accumulatedDelta = "";
  session.insertionPoint = null;
  session.openSpan = null;
  session.target = null;
  session.sealedSpans = [];
  // cancel(), not clear(): a cleanup request in flight across a stop/restart
  // would otherwise still land, rewrite the previous session's span, and shift
  // the new session's spans in that field by the delta.
  session.cleanupQueue?.cancel();
  session.cleanupQueue = null;
  notifyIfDrained();
};

// How many hook instances currently have handlers bound. The service handlers
// are only reset to no-ops when the LAST instance unmounts — otherwise closing
// the Indy panel would silently disconnect Assessment's dictation, which stays
// mounted underneath it.
let boundInstances = 0;

/**
 * Bumped by every stopDictation, from any instance. startDictation captures it
 * before its awaits; a mismatch afterward means a stop landed mid-connect, so
 * the start must not go live — it undoes the recording it just created instead
 * of overwriting the stop with setStatus("active"). Module-scoped because the
 * stop can come from a different hook instance than the start (e.g. the
 * assessment save wrapper stopping a connect the floating button began).
 */
let stopEpoch = 0;

/** Generation counter for starts: the undo in startDictation's stale branch
 *  must not fire if a newer start has since begun — stopping the service then
 *  would kill the newer session's recording, not this one's. */
let startGen = 0;

const isTargetElement = (el: unknown): el is TargetElement =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

/**
 * Resolve which field transcription should be written into: the one already
 * receiving this turn's text, else whatever the physician last focused.
 *
 * `isTargetable` is the surface's own idea of a field it may write into — the
 * pin taken here lasts the whole session, so a surface that is scoped to one
 * region of the page has to say so *before* the pin, not after. Defaults to
 * "any input or textarea", which is what the legacy Assessment path has always
 * used.
 */
const resolveTargetElement = (
  surface: DictationSurface,
): TargetElement | null => {
  const isTargetable = targetFilterFor(surface);
  const remembered = session.lastFocused[fenceKeyFor(surface)];
  // Module-scoped state outlives page navigation, so either remembered element
  // can be detached by the time the next transcript arrives. Writing into a
  // detached node silently loses the utterance — treat it as no element.
  if (session.target && !session.target.isConnected) {
    session.target = null;
  }
  if (session.target) {
    return session.target;
  }

  const activeEl = document.activeElement;
  const element = isTargetable(activeEl)
    ? activeEl
    : isTargetable(remembered) && remembered.isConnected
      ? remembered
      : null;

  if (element) {
    session.target = element;
  }
  return element;
};

/**
 * Which surfaces re-aim at the focused field part-way through a recording.
 *
 * Assessment is a form of ~8 freeform fields and physicians hop between them
 * mid-dictation, so the target has to follow. Indy has one composer, and its
 * panel opens *over* an appointment page — following focus there would drop
 * chat dictation into a report field. It pins its target instead, at the
 * gesture (`startDictation`) and fenced to the composer (`isIndyTargetable`).
 * Exam dictation pins the same way: its transcript box floats over an editable
 * exam form, and clicking a field mid-dictation must not redirect speech there.
 */
const retargetsOnFocus = (surface: DictationSurface): boolean =>
  surface === "assessment-streaming";

/**
 * A field an Assessment surface may dictate into — legacy or streaming.
 *
 * Stricter than "is an input": a checkbox is an HTMLInputElement, a readOnly
 * field takes no typing, and the Indy composer is a text field that belongs to
 * a different dictation surface entirely — the panel floats over this page and
 * its textarea carries `autoFocus`, so following focus there would write the
 * clinical note into the chat box, one Enter from being sent. Any of these
 * gaining focus reads as "not a dictation target", like a button: the open span
 * stays where it is.
 */
const isAssessmentTargetable = (el: unknown): el is TargetElement =>
  isTargetElement(el) &&
  isTextInputElement(el) &&
  el.closest(`[${INDY_COMPOSER_ATTRIBUTE}]`) === null;

/**
 * A field the Indy surface may dictate into: one inside the chat composer.
 *
 * The exact mirror of `isAssessmentTargetable`, and the other half of the
 * isolation between the two surfaces. Indy pins its target rather than
 * following focus, but a pin is only as good as what it may land on — without
 * this, the pin took whatever held focus anywhere on the appointment page
 * underneath the panel.
 */
const isIndyTargetable = (el: unknown): el is TargetElement =>
  isTargetElement(el) &&
  isTextInputElement(el) &&
  el.closest(`[${INDY_COMPOSER_ATTRIBUTE}]`) !== null;

/**
 * Which fields a surface is allowed to write into.
 *
 * The two halves partition the page: Indy may only write inside its composer,
 * the Assessment surfaces only outside it. Neither branch is decorative —
 * legacy `assessment` runs this too, and it is the surface most physicians are
 * on, since streaming is still behind a pilot flag.
 */
const targetFilterFor = (
  surface: DictationSurface,
): ((el: unknown) => el is TargetElement) =>
  surface === "indy" ? isIndyTargetable : isAssessmentTargetable;

/**
 * Run a write that needs the target focused, then hand focus back.
 *
 * `execCommand("insertText")` only acts on the focused element, so the legacy
 * Assessment path has to take focus to write at all. Taking it is invisible
 * when the physician is already in that field — which, before the surfaces
 * were fenced apart, is the only case that could arise, because the write went
 * wherever focus was. Now that the fence redirects the write back to the report
 * field, the physician can be somewhere else entirely: an utterance landing
 * while they type in the Indy composer would otherwise pull focus away and send
 * their next keystrokes into the clinical note.
 */
const withFocusRestored = (element: TargetElement, write: () => void): void => {
  const previous = document.activeElement;
  // Any focusable element, not just a text input: a checkbox or an open Select
  // left holding focus would send the physician's next Space or Enter into the
  // report field.
  const restore =
    previous instanceof HTMLElement && previous !== element ? previous : null;
  const selection = isTextInputElement(restore)
    ? ([restore.selectionStart, restore.selectionEnd] as const)
    : null;

  // preventScroll on both moves: the target is often off-screen (that is the
  // whole scenario), and without it every delta scrolls the report field into
  // view and the composer back, at delta rate.
  element.focus({ preventScroll: true });
  write();

  if (restore?.isConnected) {
    restore.focus({ preventScroll: true });
    if (
      isTextInputElement(restore) &&
      selection?.[0] != null &&
      selection[1] != null
    ) {
      restore.setSelectionRange(selection[0], selection[1]);
    }
  }
};

/**
 * The field the streaming path should write into.
 *
 * An open span owns the turn: this utterance keeps going where it started,
 * even if focus has moved, because the retarget path below closes the span
 * *first* and then lets the next delta open a fresh one.
 */
const resolveStreamingTarget = (): TargetElement | null => {
  const open = session.openSpan;
  if (open) {
    // Module state outlives navigation, so the element can be detached by now.
    if (open.target.isConnected) {
      return open.target;
    }
    session.openSpan = null;
  }
  const surface = webrtcTranscriptionService.getSurface();
  if (!retargetsOnFocus(surface)) {
    return resolveTargetElement(surface);
  }

  const activeEl = document.activeElement;
  if (isAssessmentTargetable(activeEl)) {
    return activeEl;
  }
  // Focus on something that is not a dictation target (a button, the mic, the
  // Indy composer) means "still dictating into where I was", not "stop
  // targeting". The fallback gets the same filter: better to drop an utterance
  // than to write it into a field this surface must never touch.
  const last = session.lastFocused[fenceKeyFor(surface)];
  return isAssessmentTargetable(last) && last.isConnected ? last : null;
};

/**
 * Focus moved to a different text field mid-utterance: close the utterance in
 * the field it was spoken into, so the next delta opens a span in the new one.
 *
 * This commits rather than sealing the span by hand. `sealedSpans` is paired to
 * finals strictly one-per-commit in arrival order, and an entry with no commit
 * behind it has no final coming — it would consume the *next* sentence's final
 * and shift every one after that onto the wrong sentence.
 */
const sealSpanIfFocusMoved = (next: unknown): void => {
  const surface = webrtcTranscriptionService.getSurface();
  if (!retargetsOnFocus(surface)) {
    return;
  }
  const open = session.openSpan;
  if (!open || !isAssessmentTargetable(next) || next === open.target) {
    return;
  }
  if (!webrtcTranscriptionService.getStatus().isRecording) {
    return;
  }

  webrtcTranscriptionService.commitOpenUtterance();
  if (session.openSpan) {
    // The commit could not be sent (data channel down), so no final is coming
    // for it. Abandon the span instead: what was typed stays in the old field,
    // and the accumulated text must not follow us into the new one.
    session.openSpan = null;
    session.accumulatedDelta = "";
  }
};

/**
 * Live microphone level for components that render it (the mic buttons).
 * Separate from useDictationManager so the ~15Hz level updates only re-render
 * the small components that draw them.
 */
export const useDictationAudioLevel = () => ({
  audioLevel: useAtomValue(dictationAudioLevelAtom),
  isSpeechDetected: useAtomValue(dictationSpeechDetectedAtom),
});

/** Shown when a dictation nobody was speaking into is ended automatically. */
export const DICTATION_AUTO_STOP_MESSAGE =
  "Dictation stopped — no speech detected. Click the microphone to resume.";

/**
 * Show a notice the user will actually see.
 *
 * The case this exists for is someone who started a dictation and switched
 * away, so the tab is usually hidden when the notice fires — and a toast that
 * dismisses on a timer would be long gone before they looked back, leaving the
 * mic button quietly no longer red as the only evidence. Hold it until the tab
 * is visible again.
 */
function notifyWhenVisible(text: string): void {
  if (typeof document === "undefined" || !document.hidden) {
    toast({ message: text, duration: 5000 });
    return;
  }
  const show = () => {
    if (document.hidden) return;
    document.removeEventListener("visibilitychange", show);
    toast({ message: text, duration: 5000 });
  };
  document.addEventListener("visibilitychange", show);
}

export const useDictationManager = () => {
  const { user } = useCurrentUser();
  const { isEnabled: indyCleanupEnabled } = useFeatureFlag(
    FEATURE_FLAGS.INDY_DICTATION_CLEANUP,
  );
  const { isEnabled: assessmentStreamingEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ASSESSMENT_STREAMING_DICTATION,
  );

  /**
   * Whether the LLM cleanup pass runs for the surface currently dictating.
   *
   * Indy keeps its own flag, so the streaming engine and the cleanup pass can
   * still be compared side by side there. The Assessment pilot folds cleanup
   * into its one flag instead: it is a single physician and a single decision,
   * and a half-configured pilot (streaming without the plausibility guard that
   * matters most in report content) would measure the wrong thing.
   */
  const isCleanupEnabled = useCallback(
    (): boolean =>
      webrtcTranscriptionService.getSurface() === "exam-streaming"
        ? true
        : webrtcTranscriptionService.getSurface() === "assessment-streaming"
          ? assessmentStreamingEnabled
          : indyCleanupEnabled,
    [assessmentStreamingEnabled, indyCleanupEnabled],
  );

  // Use shared Jotai atoms for state that needs to be consistent across all instances
  const [status, setStatus] = useAtom(dictationStatusAtom);
  const [activeSection, setActiveSection] = useAtom(dictationActiveSectionAtom);
  // Write-only: the level updates at ~15Hz while dictating, and a useAtom
  // subscription here re-renders every component that mounts this hook — the
  // whole ChatComposer included — at that rate. Components that render the
  // level read it through useDictationAudioLevel() instead.
  const setAudioLevel = useSetAtom(dictationAudioLevelAtom);
  const setIsSpeechDetected = useSetAtom(dictationSpeechDetectedAtom);

  // Local state for error and mic permission (these are transient/local concerns)
  const [error, setError] = useState<string | null>(null);
  const [microphonePermission, setMicrophonePermission] = useState<
    "granted" | "denied" | "prompt" | null
  >(null);

  const lastAudioUpdateRef = useRef<number>(0);

  // Store reference to the currently active element for styling
  const activeElementRef = useRef<HTMLElement | null>(null);

  /**
   * Remove visual styling from the active text input element
   */
  const removeDictationStyling = useCallback(() => {
    if (activeElementRef.current) {
      activeElementRef.current.classList.remove("dictation-active");
      activeElementRef.current = null;
    }
  }, []);

  /**
   * Check if dictation is supported in the current browser
   */
  const isSupported = isBrowserSupported();

  /**
   * Start dictation session
   * @param sectionId - Optional section identifier for tracking which section initiated dictation
   */
  const startDictation = useCallback(
    async (sectionId?: string, surface: DictationSurface = "assessment") => {
      if (!isSupported) {
        setError("Dictation not supported in this browser");
        return;
      }

      if (!user?.id) {
        setError("User not authenticated");
        return;
      }

      const epoch = stopEpoch;
      const gen = ++startGen;
      setStatus("connecting");
      setError(null);
      if (sectionId) {
        setActiveSection(sectionId);
      }

      // Reset transcript and turn tracking for new session
      resetSession();

      // Pin Indy's target now rather than letting the first delta resolve it.
      // Indy writes into one field for the whole session, but the pin used to
      // be taken at the first delta — so anything the physician focused
      // between the gesture and their first word (the connect alone is
      // 2.5-4s) became the target, and on an appointment page under the
      // floating panel that meant a report field, permanently. Focus is on
      // the composer at *this* instant on every gesture: click and double-tap
      // focus it, and push-to-talk is gated on it already holding focus. The
      // exam mic focuses its transcript box before starting, for the same pin.
      if (surface === "indy" || surface === "exam-streaming") {
        resolveTargetElement(surface);
      }

      try {
        // Check microphone permission
        const permission = await checkMicrophonePermission();
        setMicrophonePermission(permission);

        if (permission === "denied") {
          throw new Error(
            "Microphone access is denied. Please enable microphone permissions for this site.",
          );
        }

        // A stop during the permission prompt already put the UI back to
        // inactive; going further would overwrite it.
        if (epoch !== stopEpoch) return;

        // Start recording (handles connection establishment/reuse internally)
        await webrtcTranscriptionService.startRecording(surface);

        // A stop during the 2.5-4s connect used to be silently undone here:
        // the stop had already run, then this continuation flipped the status
        // back to active — a live microphone on a surface that thinks it is
        // idle, possibly with its stop control unmounted. Undo the recording
        // instead.
        if (epoch !== stopEpoch) {
          // Only undo if no newer start has begun; otherwise the recording
          // now belongs to that newer session.
          if (gen === startGen) {
            webrtcTranscriptionService.stopRecording();
          }
          return;
        }

        setStatus("active");
      } catch (err) {
        console.error("Failed to start dictation:", err);
        setError(
          err instanceof Error ? err.message : "Failed to start dictation",
        );
        setStatus("inactive");
        setActiveSection(null);
      }
    },
    [isSupported, user?.id, setStatus, setActiveSection],
  );

  /**
   * Stop dictation session. Connection stays open for fast restart.
   */
  const stopDictation = useCallback(() => {
    stopEpoch++;
    setStatus("stopping");

    // Stop recording (connection stays open for reuse). On the streaming
    // surface this flushes the open utterance, so its final transcript is
    // still in flight when we return.
    webrtcTranscriptionService.stopRecording();

    if (isStreamingSurface(webrtcTranscriptionService.getSurface())) {
      // The stop-flush commit has already sealed the open span (the service
      // fires onUtteranceCommitted synchronously from stopRecording), so the
      // in-flight final has a sealed span to land on. Nothing to clear here:
      // startDictation resets everything when the next session begins, so
      // nothing leaks if the final never arrives.
      session.currentTranscript = "";
    } else {
      // Assessment: reset delta tracking to prevent partial text from
      // lingering, exactly as it always has. Its finals arrive before stop
      // (server VAD closes each turn), so there is nothing in flight to keep
      // a span for.
      session.currentTranscript = "";
      session.accumulatedDelta = "";
      session.insertionPoint = null;
      session.target = null;
    }

    // Remove visual styling
    removeDictationStyling();

    // Reset audio visualization state
    setAudioLevel(0);
    setIsSpeechDetected(false);

    setStatus("inactive");
    setActiveSection(null);
  }, [
    removeDictationStyling,
    setAudioLevel,
    setIsSpeechDetected,
    setStatus,
    setActiveSection,
  ]);

  /**
   * Stop, then wait for what is still in flight to land.
   *
   * Streaming finals arrive *after* the stop — the stop-flush commit seals the
   * open span and its corrected sentence comes back a beat later. A save that
   * serialised immediately would write the raw text and then be re-dirtied by
   * the correction, so a caller that is about to read the form waits here
   * first. Bounded: on timeout we save what is on screen, which is the raw but
   * complete transcript, never a truncated one.
   *
   * Whatever polish fits in the remaining budget is kept; the rest is
   * abandoned rather than left to rewrite a field the physician has already
   * committed and left. Legacy Assessment returns straight after the stop —
   * its server VAD closed every turn before the stop, so nothing is in flight.
   */
  const flushDictation = useCallback(
    async (timeoutMs: number = FLUSH_TIMEOUT_MS): Promise<void> => {
      const streaming = isStreamingSurface(
        webrtcTranscriptionService.getSurface(),
      );
      const deadline = Date.now() + timeoutMs;
      // A dictation restarted during this flush owns the session state from
      // that moment on — its spans, its queue. A generation mismatch below
      // means the debris this flush was waiting on is gone and the queue now
      // holds the new session's work, which must not be cancelled.
      const gen = startGen;

      // Nothing to stop when the mic was already clicked off — this flush is
      // then only settling the finals and cleanups that stop left in flight.
      if (status !== "inactive") stopDictation();
      if (!streaming) return;

      await waitForSealedSpans(timeoutMs);
      if (gen !== startGen) return;

      // Read the queue only now: it is built lazily by the first final's
      // enqueue, so on a page's first dictation it does not exist until the
      // wait above has let that final land. A snapshot taken before the wait
      // reads null and skips the cancel — the exact post-save rewrite the
      // generation counter exists to prevent.
      const queue = session.cleanupQueue;
      if (queue) {
        await Promise.race([
          queue.idle(),
          delay(Math.max(0, deadline - Date.now())),
        ]);
        if (gen === startGen) queue.cancel();
      }

      // Those writes reached the field as input events; a caller like the
      // assessment save then reads form state that a `useEffect` mirrors from
      // them, which React commits on its own schedule. Yield a macrotask so
      // that has happened first.
      //
      // Defensive, and deliberately unpinned by a test: under jsdom `act()`
      // flushes effects synchronously, so a test cannot tell this yield from
      // its absence — one written to "cover" it would pass either way.
      await delay(0);
    },
    [status, stopDictation],
  );

  /**
   * Toggle dictation on/off
   * @param sectionId - Optional section identifier for tracking which section initiated dictation
   */
  const toggleDictation = useCallback(
    async (sectionId?: string, surface: DictationSurface = "assessment") => {
      if (status === "active") {
        stopDictation();
      } else if (status === "inactive") {
        await startDictation(sectionId, surface);
      }
      // "connecting" is neither: nothing is active to stop, and starting would
      // open a second session behind the one still coming up — and the 2.5-4s
      // cold connect shows no feedback, which is exactly when a user triggers
      // the toggle again. Guarded here once so no caller (chord, double-tap,
      // button) needs its own isConnecting gate. ("stopping" is transient
      // inside stopDictation and never observable here.)
    },
    [status, startDictation, stopDictation],
  );

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Configure speech detection thresholds for non-speech filtering
   */
  const configureSpeechThresholds = useCallback(
    (thresholds: Partial<SpeechThresholds>) => {
      webrtcTranscriptionService.setSpeechThresholds(thresholds);
    },
    [],
  );

  // ─── Assessment path — the insertion behaviour this hook has always had ───
  // These three are the pre-streaming implementation: execCommand insertion at
  // the cursor, focus taken on write, and the target re-resolved from focus
  // after every utterance. Its insertion behaviour is unchanged; what it no
  // longer does is resolve that target unfenced. Re-resolving every utterance
  // means the Indy panel opening mid-dictation (its textarea autofocuses) used
  // to capture the NEXT sentence into the chat box.

  /**
   * Helper to insert text at a specific position in an input/textarea
   * Uses execCommand for React compatibility with controlled components
   */
  const insertTextAtPosition = useCallback(
    (element: TargetElement, text: string, position: number) => {
      const before = element.value.slice(0, position);

      // Simple spacing: add space before if needed
      let textToInsert = text;
      if (before.length > 0 && !before.match(/\s$/)) {
        textToInsert = " " + text;
      }

      // Focus, set the cursor, insert — execCommand acts on the focused
      // element, so the focus is the mechanism, not a side effect.
      withFocusRestored(element, () => {
        element.setSelectionRange(position, position);
        document.execCommand("insertText", false, textToInsert);
      });

      // Return the actual length inserted (including any added space)
      return textToInsert.length;
    },
    [],
  );

  const handleAssessmentDelta = useCallback((delta: string) => {
    const element = resolveTargetElement(
      webrtcTranscriptionService.getSurface(),
    );
    if (!element || !delta) return;

    // On first delta of an utterance, capture the insertion point and add spacing
    let textToInsert = delta;
    if (session.insertionPoint === null) {
      session.insertionPoint = element.selectionStart ?? element.value.length;
      session.accumulatedDelta = "";

      // Add space before first delta if needed
      const before = element.value.slice(0, session.insertionPoint);
      if (before.length > 0 && !before.match(/\s$/)) {
        textToInsert = " " + delta;
      }
    }

    // Focus element and use execCommand for React-compatible insertion
    withFocusRestored(element, () => {
      document.execCommand("insertText", false, textToInsert);
    });

    // Track accumulated text
    session.accumulatedDelta += textToInsert;
  }, []);

  const handleAssessmentFinal = useCallback(
    (transcript: string) => {
      if (!transcript.trim()) {
        // Empty transcript, just reset
        session.accumulatedDelta = "";
        session.insertionPoint = null;
        session.target = null;
        return;
      }

      const element = resolveTargetElement(
        webrtcTranscriptionService.getSurface(),
      );
      if (!element) {
        // No target element - reset and skip
        session.accumulatedDelta = "";
        session.insertionPoint = null;
        session.target = null;
        return;
      }

      const accumulated = session.accumulatedDelta.trim();
      const final = transcript.trim();

      if (session.insertionPoint !== null && accumulated === final) {
        // Deltas matched final - nothing to do, text already inserted
      } else if (session.insertionPoint !== null && accumulated !== final) {
        // Mismatch - replace accumulated text with final transcript
        const before = element.value.slice(0, session.insertionPoint);
        const accumulatedWithSpacing = session.accumulatedDelta;
        const insertEnd =
          session.insertionPoint + accumulatedWithSpacing.length;
        const after = element.value.slice(insertEnd);

        // Add spacing if needed
        let textToInsert = final;
        if (before.length > 0 && !before.match(/\s$/)) {
          textToInsert = " " + final;
        }

        element.value = before + textToInsert + after;

        // Trigger React's onChange
        const inputEvent = new Event("input", { bubbles: true });
        element.dispatchEvent(inputEvent);

        // Update cursor position
        const newCursorPos = session.insertionPoint + textToInsert.length;
        element.setSelectionRange(newCursorPos, newCursorPos);
      } else {
        // No deltas received - insert final transcript directly
        const cursorPos = element.selectionStart ?? element.value.length;
        insertTextAtPosition(element, final, cursorPos);
      }

      session.currentTranscript = transcript;

      // Reset tracking for next utterance
      session.accumulatedDelta = "";
      session.insertionPoint = null;
      session.target = null;
    },
    [insertTextAtPosition],
  );

  // ─── Streaming path — writes into tracked spans, one per field ───

  /**
   * Commit a span rewrite (a final landing on its sealed span, or a cleaned
   * utterance replacing it), keeping everything tracked by offset in step.
   *
   * The rewrite lands somewhere behind the caret, so anything recorded at a
   * higher offset — the open span, later sealed spans, the caret itself — has
   * moved by the same amount.
   */
  const applySpanRewrite = useCallback(
    (
      element: TargetElement,
      value: string,
      at: number,
      shift: number,
      newLength: number,
    ) => {
      const focused = document.activeElement === element;
      const caret = element.selectionStart ?? 0;

      setInputValue(element, value, "dictation");

      // Only spans in the rewritten field move: an offset means nothing
      // outside the field it was recorded in.
      const shiftInElement = (span: Span | null): Span | null =>
        span && span.target === element
          ? { ...span, region: shiftRegion(span.region, at, shift) }
          : span;
      session.openSpan = shiftInElement(session.openSpan);
      session.sealedSpans = session.sealedSpans.map(shiftInElement);

      // As in writeStreamingTranscript: only move the caret when the field
      // actually has it, so a physician who has clicked away is left alone.
      // A caret after the rewrite moves with it; one inside the rewritten span
      // clamps to the span's new end — a flat +shift on a shrinking rewrite
      // would throw it backwards into the previous sentence.
      if (focused) {
        const oldEnd = at + newLength - shift;
        const next =
          caret >= oldEnd
            ? caret + shift
            : caret > at
              ? Math.min(caret, at + newLength)
              : caret;
        element.setSelectionRange(next, next);
      }
    },
    [],
  );

  /** The cleanup queue for this session, built on first use. */
  const cleanupQueue = useCallback((): DictationCleanupQueue<TargetElement> => {
    if (!session.cleanupQueue) {
      const surface = webrtcTranscriptionService.getSurface();
      session.cleanupQueue = new DictationCleanupQueue<TargetElement>({
        clean: (utterance, precedingText) =>
          surface === "exam-streaming"
            ? cleanDictationUtterance(utterance, precedingText, surface)
            : cleanDictationUtterance(utterance, precedingText),
        // A field the physician has navigated away from is gone, not empty —
        // reading "" would look like they cleared it and cancel the rewrite
        // for the wrong reason.
        readField: (element) => (element.isConnected ? element.value : null),
        writeField: applySpanRewrite,
      });
    }
    return session.cleanupQueue;
  }, [applySpanRewrite]);

  /**
   * Write `text` for one turn, replacing what the previous delta wrote.
   *
   * The cursor is only moved when the field is actually focused — a streaming
   * turn runs for many seconds and stealing focus back on every delta would
   * fight a physician who has clicked away.
   */
  const writeStreamingTranscript = useCallback(
    (text: string): void => {
      const element = resolveStreamingTarget();
      if (!element) {
        session.accumulatedDelta = "";
        session.openSpan = null;
        return;
      }

      // Where a fresh utterance starts. The caret is only trustworthy while
      // the field is focused — after a click away, selectionStart is wherever
      // the caret happened to be, and splicing there would drop the utterance
      // into the middle of existing text. Unfocused fields append at the end.
      const insertionCursor =
        document.activeElement === element
          ? (element.selectionStart ?? element.value.length)
          : element.value.length;

      const placement = placeTranscript({
        value: element.value,
        cursor: insertionCursor,
        region: session.openSpan?.region ?? null,
        text,
        // Stray mid-sentence capitals come from committing utterances
        // separately, which only the streaming surface does.
        //
        // Stood down when the cleanup pass is on: it decides capitalisation
        // properly, with the whole preceding sentence and no lower-case-tail
        // heuristic. Leaving both in place would let the regex's guess reach
        // the model as if it were what was transcribed, biasing the answer it
        // is there to give.
        repairContinuationCase: !isCleanupEnabled(),
      });

      setInputValue(element, placement.value, "dictation");
      session.openSpan = { target: element, region: placement.region };

      if (document.activeElement === element) {
        element.setSelectionRange(placement.cursor, placement.cursor);
      }
    },
    [isCleanupEnabled],
  );

  /**
   * Seal the open span: a commit was sent, so this utterance's text is fixed
   * and its final is on the way. The next delta opens a fresh span *after* it,
   * which is what keeps final N from touching the words of utterance N+1.
   */
  const handleUtteranceCommitted = useCallback(() => {
    if (!isStreamingSurface(webrtcTranscriptionService.getSurface())) {
      return;
    }
    session.sealedSpans.push(session.openSpan);
    session.accumulatedDelta = "";
    session.openSpan = null;
  }, []);

  const handleStreamingDelta = useCallback(
    (delta: string) => {
      if (!delta) return;
      // Deltas after recording ended are trailing fragments of an utterance
      // whose sealed span the final will replace wholesale — writing them
      // would open a junk span in a field the physician may have moved on
      // from (sent, cleared, mid-edit).
      if (!webrtcTranscriptionService.getStatus().isRecording) {
        return;
      }

      // Backstop for the focusin listener: a focus change it did not see (a
      // programmatic focus, a listener not yet bound) must still close the
      // utterance in the old field before this delta is accumulated into it.
      sealSpanIfFocusMoved(document.activeElement);

      // Deltas append within an utterance, so the accumulated text is what the
      // field should hold — rewritten in place rather than appended to, because
      // the final transcript may revise any of it. A turn that has produced
      // only whitespace has nothing to say yet — writing it would open a
      // space-only span that outlives its (empty) final as stray gaps.
      session.accumulatedDelta += delta;
      if (!session.accumulatedDelta.trim()) {
        return;
      }
      writeStreamingTranscript(session.accumulatedDelta);
    },
    [writeStreamingTranscript],
  );

  /**
   * Handle the authoritative transcript for a completed turn. This is where the
   * sentence reformats: OpenAI's final transcript carries the punctuation,
   * casing and corrections that the streamed deltas lacked, so it replaces them.
   */
  const handleStreamingFinal = useCallback(
    (transcript: string) => {
      const final = transcript.trim();
      if (!final) {
        // An empty final still consumes its sealed span, or the next real
        // final would land on the wrong sentence.
        session.sealedSpans.shift();
        notifyIfDrained();
        return;
      }

      // The span this final belongs to: the oldest sealed entry (one exists
      // per commit; null when that utterance never wrote — its final is
      // dropped). Only when nothing was sealed at all — OpenAI flushed its
      // buffer without a client commit — is the open span this utterance's
      // text, sealed on demand. A final with no span anywhere has nothing on
      // screen to correct and is dropped: writing it anyway is how a late
      // final used to resurrect already-sent text, or duplicate itself after
      // a quick restart.
      let span: Span | null;
      if (session.sealedSpans.length > 0) {
        span = session.sealedSpans.shift() ?? null;
        notifyIfDrained();
      } else {
        span = session.openSpan;
        session.accumulatedDelta = "";
        session.openSpan = null;
      }
      if (!span) {
        return;
      }

      // The span's own field, not whichever one is current: the physician may
      // have moved on to the next question while this sentence was in flight.
      const element = span.target;
      if (!element.isConnected || !isRegionIntact(element.value, span.region)) {
        // Sent, cleared, unmounted, or edited over while the final was in
        // flight — the text has moved on without us.
        return;
      }

      // The repair the cleanup pass makes redundant — see writeStreamingTranscript.
      const text = isCleanupEnabled()
        ? final
        : decapitalizeContinuation(
            final,
            element.value.slice(0, span.region.start),
          );

      const replacement = replaceRegion(element.value, span.region, text);
      if (!replacement) {
        return;
      }
      applySpanRewrite(
        element,
        replacement.value,
        span.region.start,
        replacement.shift,
        replacement.region.text.length,
      );
      session.currentTranscript = transcript;

      // …and hand the span to the cleanup pass, which rewrites it again once a
      // model has seen it alongside the text it follows. Transcription is
      // per-utterance and therefore context-blind: only this second pass can
      // tell a fragment continuing a sentence from one starting a new one. The
      // raw text is already on screen, so the round trip costs polish arriving
      // a beat late rather than any delay in seeing what was said.
      if (isCleanupEnabled()) {
        cleanupQueue().enqueue({ target: element, region: replacement.region });
      }
    },
    [isCleanupEnabled, cleanupQueue, applySpanRewrite],
  );

  // ─── Surface dispatch ───

  const handleTranscriptDelta = useCallback(
    (delta: string) => {
      if (isStreamingSurface(webrtcTranscriptionService.getSurface())) {
        handleStreamingDelta(delta);
      } else {
        handleAssessmentDelta(delta);
      }
    },
    [handleStreamingDelta, handleAssessmentDelta],
  );

  const handleTranscriptFinal = useCallback(
    (transcript: string) => {
      if (isStreamingSurface(webrtcTranscriptionService.getSurface())) {
        handleStreamingFinal(transcript);
      } else {
        handleAssessmentFinal(transcript);
      }
    },
    [handleStreamingFinal, handleAssessmentFinal],
  );

  // Legacy handler for backwards compatibility (no longer used for insertion)
  const handleTranscript = useCallback(
    (_event: { text: string; isFinal: boolean }) => {
      // Delta and final handling now done via separate callbacks
      // This handler kept for backwards compatibility but is essentially a no-op
    },
    [],
  );

  const handleError = useCallback(
    (err: Error) => {
      console.error("Transcription error:", err);

      // Remove visual styling
      removeDictationStyling();

      setError(err.message);
      setStatus("inactive");
      setActiveSection(null);
    },
    [removeDictationStyling, setStatus, setActiveSection],
  );

  const handleConnected = useCallback(() => {
    // Only transition to "active" if user explicitly started dictation.
    // Preconnect also fires onConnected, but status is "inactive" — ignore it.
    setStatus((prev) => (prev === "connecting" ? "active" : prev));
  }, [setStatus]);

  const handleDisconnected = useCallback(() => {
    // WebRTC connection closed

    // Remove visual styling when disconnected
    removeDictationStyling();

    setStatus("inactive");
    setActiveSection(null);
  }, [removeDictationStyling, setStatus, setActiveSection]);

  const handleRecordingStarted = useCallback(() => {
    setStatus("active");
  }, [setStatus]);

  const handleRecordingStopped = useCallback(() => {
    // Recording stopped, but we stay in active state until explicitly stopped
  }, []);

  // A dictation nobody is speaking into. Runs the ordinary stop path so the
  // open utterance is still flushed and the UI lands in the same state a
  // manual stop leaves it in — the user simply did not press the button.
  const handleAutoStopped = useCallback(() => {
    stopDictation();
    notifyWhenVisible(DICTATION_AUTO_STOP_MESSAGE);
  }, [stopDictation]);

  // Throttled audio analysis handler for visualization
  const handleAudioAnalysis = useCallback(
    (data: AudioAnalysisData) => {
      const now = Date.now();
      if (now - lastAudioUpdateRef.current >= AUDIO_ANALYSIS_THROTTLE_MS) {
        setAudioLevel(data.rms);
        setIsSpeechDetected(data.containsSpeech);
        lastAudioUpdateRef.current = now;
      }
    },
    [setAudioLevel, setIsSpeechDetected],
  );

  // Set up transcription service event handlers.
  //
  // Every mounted instance binds; the last binder wins. That is safe because
  // every instance's handlers close over the same module-scoped session and
  // the same Jotai atoms — they are interchangeable. The cleanup only resets
  // the handlers when the LAST instance unmounts, so closing the Indy panel
  // never disconnects Assessment's dictation underneath it.
  useEffect(() => {
    boundInstances++;

    webrtcTranscriptionService.onTranscriptReceived = handleTranscript;
    webrtcTranscriptionService.onTranscriptDelta = handleTranscriptDelta;
    webrtcTranscriptionService.onTranscriptFinal = handleTranscriptFinal;
    webrtcTranscriptionService.onUtteranceCommitted = handleUtteranceCommitted;
    webrtcTranscriptionService.onError = handleError;
    webrtcTranscriptionService.onConnected = handleConnected;
    webrtcTranscriptionService.onDisconnected = handleDisconnected;
    webrtcTranscriptionService.onRecordingStarted = handleRecordingStarted;
    webrtcTranscriptionService.onRecordingStopped = handleRecordingStopped;
    webrtcTranscriptionService.onAutoStopped = handleAutoStopped;
    webrtcTranscriptionService.onAudioAnalysis = handleAudioAnalysis;

    return () => {
      boundInstances--;
      if (boundInstances > 0) {
        return;
      }
      webrtcTranscriptionService.onTranscriptReceived = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onTranscriptDelta = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onTranscriptFinal = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onUtteranceCommitted = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onError = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onConnected = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onDisconnected = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onRecordingStarted = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onRecordingStopped = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onAutoStopped = () => {
        /* no-op */
      };
      webrtcTranscriptionService.onAudioAnalysis = () => {
        /* no-op */
      };
    };
  }, [
    handleTranscript,
    handleTranscriptDelta,
    handleTranscriptFinal,
    handleUtteranceCommitted,
    handleError,
    handleConnected,
    handleDisconnected,
    handleRecordingStarted,
    handleRecordingStopped,
    handleAutoStopped,
    handleAudioAnalysis,
  ]);

  // Track focus changes to always know which text input is active
  useEffect(() => {
    const handleFocusChange = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      // Close the open utterance in the field it was spoken into before the
      // next delta lands, so the seal happens when the physician moves rather
      // than a word later. Every mounted instance runs this; the first commit
      // clears the open span, so the rest see nothing to do.
      sealSpanIfFocusMoved(target);
      // File the field under the fence that accepts it, and keep both. The
      // two fences partition every text input — inside the composer, or not —
      // so each surface's fallback survives the other surface taking focus.
      // Deliberately no reference to the current surface: it is sticky
      // between sessions, and reading it here files pre-gesture focus under
      // whichever surface happened to run last.
      if (isIndyTargetable(target)) {
        session.lastFocused.indy = target;
      } else if (isAssessmentTargetable(target)) {
        session.lastFocused.page = target;
      }
    };

    // Listen for focus events globally
    document.addEventListener("focusin", handleFocusChange, true);

    return () => {
      document.removeEventListener("focusin", handleFocusChange, true);
    };
  }, []);

  // Clean up styling on unmount.
  // NOTE: Connection lifecycle (stopSession) is owned by usePreconnectDictation,
  // which must be mounted as an ancestor whenever this hook is used.
  useEffect(() => {
    return () => {
      removeDictationStyling();
    };
  }, [removeDictationStyling]);

  return {
    // State
    isSupported,
    isActive: status === "active",
    isConnecting: status === "connecting",
    isRecording: status === "active",
    error,
    microphonePermission,
    activeSection, // Which section initiated the current dictation

    // Actions
    startDictation,
    stopDictation,
    flushDictation,
    toggleDictation,
    clearError,
    configureSpeechThresholds,

    // Status helpers
    canStart: status === "inactive" && isSupported,
    statusText:
      status === "connecting"
        ? "Connecting..."
        : status === "active"
          ? "Recording - Speak now"
          : "Not active",
  };
};
